import React, { forwardRef } from "react";
import { Typography, Button, Box, Stack, IconButton } from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import RefreshIcon from "@mui/icons-material/Refresh";
import HomeIcon from "@mui/icons-material/Home";
import { logError } from "../../functions/logError";

// Create an ErrorBoundary that properly handles refs when used with Gatsby router
class ErrorBoundaryClass extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console
    console.error("React Error Boundary caught an error:", error, errorInfo);

    // Log the error to Strapi
    /* too many
    logError(
      {
        message: error.message || "React component error",
        stack: error.stack,
        params: errorInfo,
        code: error.code || "REACT_ERROR",
      },
      null, // No user ID available in this context
      "ErrorBoundaryClass componentDidCatch"
    );
    */

    // Update state to store error info for better debugging UI
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      // Get current path to help with user navigation options
      const currentPath =
        typeof window !== "undefined" ? window.location.pathname : "";
      const isCartPage = currentPath.includes("/cart");

      return (
        <Box
          sx={{
            padding: "2rem",
            textAlign: "center",
            maxWidth: "600px",
            margin: "0 auto",
            marginTop: "2rem",
          }}
        >
          <Typography
            variant="h4"
            component="h2"
            gutterBottom
            sx={{ color: "primary.dark" }}
          >
            Oops! Something went wrong.
          </Typography>

          <Typography variant="body1" paragraph sx={{ color: "primary.dark" }}>
            We're sorry for the inconvenience. Please try refreshing the page or
            click on one of the buttons below.
          </Typography>

          <Stack
            direction="row"
            spacing={2}
            justifyContent="center"
            sx={{ mt: 3 }}
          >
            <Button
              variant="contained"
              sx={{
                bgcolor: "primary.dark",
                "&:hover": {
                  bgcolor: "primary.main",
                },
              }}
              startIcon={<RefreshIcon />}
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </Button>

            <Button
              variant="outlined"
              sx={{
                color: "primary.dark",
                borderColor: "primary.dark",
                "&:hover": {
                  borderColor: "primary.main",
                  color: "primary.main",
                },
              }}
              startIcon={<HomeIcon />}
              onClick={() => {
                window.location.href = "/";
              }}
            >
              Home
            </Button>

            {!isCartPage && (
              <IconButton
                sx={{
                  color: "primary.dark",
                  bgcolor: "background.paper",
                  boxShadow: 1,
                  "&:hover": {
                    bgcolor: "background.paper",
                    boxShadow: 2,
                  },
                }}
                size="large"
                onClick={() => {
                  this.setState({ hasError: false });
                  window.location.href = "/cart";
                }}
                aria-label="Go to cart"
              >
                <ShoppingCartIcon fontSize="large" />
              </IconButton>
            )}
          </Stack>

          {/* Show technical details in development environment */}
          {process.env.NODE_ENV === "development" && this.state.errorInfo && (
            <Box
              sx={{
                mt: 4,
                textAlign: "left",
                fontSize: "0.8rem",
                color: "text.secondary",
              }}
            >
              <Typography variant="h6" component="h3" gutterBottom>
                Technical Details (Development Only)
              </Typography>
              <pre
                style={{
                  overflow: "auto",
                  padding: "1rem",
                  background: "#f5f5f5",
                  borderRadius: "4px",
                }}
              >
                {this.state.errorInfo.componentStack}
              </pre>
            </Box>
          )}
        </Box>
      );
    }

    return this.props.children;
  }
}

// Create a forwardRef wrapper around your ErrorBoundary
// This is the key fix for the Gatsby router compatibility
const ErrorBoundary = forwardRef((props, ref) => {
  return <ErrorBoundaryClass {...props} ref={ref} />;
});

export default ErrorBoundary;
